import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { translations } from "./translations";

const LanguageContext = createContext();

// Supported languages - ALL from translations.js
const supportedLanguages = [
  'en', 'fr', 'ru', 'uk', 'de', 'it', 'es', 'pt', 'nl', 'cs', 
  'pl', 'hu', 'da', 'fi', 'sv', 'no', 'sk', 'tr', 'el', 'zh', 'ja', 'ar', 'hi'
];

// Country to language mapping
const countryToLanguage = {
  RU: 'ru', UA: 'uk', BY: 'ru', KZ: 'ru',
  FR: 'fr', BE: 'fr', CH: 'fr', CA: 'fr', MC: 'fr',
  DE: 'de', AT: 'de', LI: 'de',
  IT: 'it', SM: 'it', VA: 'it',
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es',
  CN: 'zh', TW: 'zh', HK: 'zh', SG: 'zh',
  JP: 'ja',
  TR: 'tr',
  SA: 'ar', AE: 'ar', EG: 'ar', QA: 'ar', KW: 'ar',
  PT: 'pt', BR: 'pt',
  NL: 'nl',
  PL: 'pl',
  CZ: 'cs',
  HU: 'hu',
  DK: 'da',
  FI: 'fi',
  SE: 'sv',
  NO: 'no',
  SK: 'sk',
  GR: 'el',
  IN: 'hi',
};

// Deep merge for fallback
const deepMerge = (target, source) => {
  const output = { ...target };
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      output[key] = deepMerge(output[key] || {}, source[key]);
    } else {
      output[key] = source[key];
    }
  }
  return output;
};

// Get translations with English fallback
const getTranslations = (lang) => {
  const langTranslations = translations[lang];
  if (!langTranslations) return translations.en;
  
  // Merge with English for missing keys
  return deepMerge(translations.en, langTranslations);
};

// Safe localStorage wrapper
const safeStorage = {
  get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  set: (key, val) => { try { localStorage.setItem(key, val); } catch {} }
};

// Detect browser language
const detectBrowserLanguage = () => {
  try {
    const browserLanguages = navigator.languages || [navigator.language || 'en'];
    for (const browserLang of browserLanguages) {
      const baseLang = browserLang.split('-')[0].toLowerCase();
      if (supportedLanguages.includes(baseLang)) {
        return baseLang;
      }
    }
  } catch {}
  return 'en';
};

// Detect language by IP
const detectLanguageByIP = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const response = await fetch('https://ipapi.co/json/', { 
      signal: controller.signal,
      mode: 'cors'
    });
    clearTimeout(timeoutId);
    if (!response.ok) return null;
    const data = await response.json();
    const detectedLang = countryToLanguage[data.country_code];
    if (detectedLang && supportedLanguages.includes(detectedLang)) {
      return detectedLang;
    }
  } catch {
    // IP detection may fail due to CORS, ad blockers, etc. - this is expected
  }
  return null;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const stored = safeStorage.get("language");
    return supportedLanguages.includes(stored) ? stored : 'en';
  });

  // No auto-detection - English by default, user can switch manually

  // Update document attributes
  useEffect(() => {
    safeStorage.set("language", language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  // Merge once per language, not once for every string rendered on a page.
  const trans = useMemo(() => getTranslations(language), [language]);
  const t = useCallback((key) => {
    const keys = key.split(".");
    let value = trans;
    
    for (const k of keys) {
      if (value && typeof value === "object") {
        value = value[k];
      } else {
        return key;
      }
    }
    
    return value || key;
  }, [trans]);

  const changeLanguage = useCallback((lang) => {
    if (supportedLanguages.includes(lang)) {
      setLanguage(lang);
      safeStorage.set("languageManuallySet", "true");
    }
  }, []);

  const contextValue = useMemo(() => ({
    language,
    currentLanguage: language,
    changeLanguage,
    t,
    supportedLanguages
  }), [language, changeLanguage, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
